import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { parseISO, format } from 'date-fns';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ItemTable } from 'src/styles/commonStyledComponents';
import { motion } from 'framer-motion';
import { UpDownArrow } from 'src/components/SearchableDropdown';
import { Breadcrumbs } from 'src/components/Breadcrumbs';
import { UnformInput } from '../../components/Input';
import LoadingIndicator from '../../components/LoadingIndicator';
import { backendUrl } from '../../config';
import { useAxiosRequest } from '../../hooks/hooks';
import {
  Page,
  Column,
  Label,
  Cell,
  Row,
  LinkLabel,
  Icon,
} from '../../components/Layout';

const BarLabel = styled.div`
  line-height: 1.2;
  margin-bottom: 0.9em;
  font-weight: bold;
`;

const Bar = styled.div`
  background-color: #2b489a;
  width: ${({ progress }) => progress};
  height: 1.2em;
  margin-bottom: 0.9em;
  position: relative;
  white-space: nowrap;

  :after {
    display: block;
    content: ${({ label }) => `'${label}'`};
    position: absolute;
    right: 0;
    transform: translateX(100%) translateX(0.9em);
  }
`;

const HeaderAnimal = styled.td`
  :hover {
    cursor: pointer;
  }
`;

const textCase = (text) => text && text.charAt(0).toUpperCase() + text.slice(1);

const MyCattle = () => {
  const [retry, setRetry] = useState(1);
  const [query, setQuery] = useState('');

  const [expandedIndexes, setExpandedIndexes] = useState({});
  const [request] = useState({
    method: 'GET',
    url: `${backendUrl}/api/my_cattle`,
    withCredentials: true,
  });
  const {
    data: { my_cattle: data },
    loading,
    error,
  } = useAxiosRequest(request, retry);

  const filterCattle = useCallback(
    (animal) => {
      if (!query.trim().length) {
        return true;
      }

      if (
        animal.cattle_type
        && animal.cattle_type.toLowerCase().includes(query.toLowerCase())
      ) {
        return true;
      }

      if (
        animal.breeds
        && animal.breeds.reduce(
          (alreadyMatch, breed) => alreadyMatch
            || breed.name.toLowerCase().includes(query.toLowerCase()),
          false,
        )
      ) {
        return true;
      }

      if (
        animal.veal_date
        && format(parseISO(animal.veal_date), 'dd.MM.yyyy').includes(query)
      ) {
        return true;
      }

      if (
        animal.birthdate
        && format(parseISO(animal.birthdate), 'dd.MM.yyyy').includes(query)
      ) {
        return true;
      }

      if (
        animal.mothers_mark
        && animal.individual_no
        && query.match(/\d*/g).join('').length
        && `${animal.mothers_mark}${animal.individual_no}`.includes(
          query.match(/\d*/g).join(''),
        )
      ) {
        return true;
      }

      return false;
    },
    [query],
  );

  const filteredData = data && data.animals.filter(filterCattle);

  const maxStock = (data
      && data.stock
      && Math.max(0, ...data.stock.map(({ count }) => count)))
    || 0;

  const expandCollapseVariants = {
    visible: {
      height: 'auto',
      oveflow: 'visible',
      transition: { duration: 0.2 },
    },
    collapsed: {
      height: 0,
      overflow: 'hidden',
    },
  };

  const onToggleExpandHide = (key) => {
    setExpandedIndexes((old) => ({ ...old, [key]: old[key] ? false : true }));
  };

  return (
    <Page
      title="Mine storfe"
      style={{
        maxWidth: '1000px',
        alignSelf: 'center',
        minWidth: '291px',
        width: '100%',
      }}
    >

      <Breadcrumbs
        breadcrumbs={[{ title: 'Mine storfe', url: '/cattle' }]}
        wrapperStyle={css`
            margin-bottom: 1.5rem;
            margin-left: 3.3rem;
            `}
      />

      <Label
        marginLeft="3.25rem"
        as="h1"
        size="2.5em"
        marginBottom="1.3em"
      >
        Mine storfe
      </Label>
      {(loading && (
        <LoadingIndicator
          withText="Laster..."
          style={{ position: 'absolute', top: '2em' }}
        />
      )) || (
        <Column
          margin="0 0.9em"
          padding="1.3em 0.9em"
          marginBottom="0.9em"
          background="#f7f6f5"
        >
          {data && data.stock && (
            <div>
              <Label size="1.5em" color="#68696d" marginBottom="0.9em">
                Oversikt
              </Label>
              <Row>
                <Column paddingRight="1.3em">
                  {data.stock.map(({ cattle_type }, key) => (
                    <BarLabel noWrap key={key}>
                      <Label bold>
                        {textCase(cattle_type)}
                      </Label>
                    </BarLabel>
                  ))}
                </Column>
                <Column grow paddingRight="5em">
                  {data.stock.map(({ count }, key) => (
                    <Bar
                      progress={`${(count / maxStock) * 100}%`}
                      label={`${count} stk`}
                    />
                  ))}
                </Column>
              </Row>
            </div>
          )}
        </Column>
      )}
      {data && (
        <>
          <Cell padding="0 1.3em">
            <UnformInput
              name="search"
              placeholder="Søk..."
              onChange={(val) => {
                setQuery(val);
                setExpandedIndexes({});
              }}
            />
          </Cell>
          {query && (
            <Label marginLeft="1.2em">
              Søket ga
              {' '}
              {filteredData.length}
              {' '}
              resultater.
            </Label>
          )}

          {filteredData.map(
            (
              {
                individual_no,
                mothers_mark,
                cattle_type,
                animal_keeping_no,
                breeds,
                veal_date,
                birthdate,
              },
              key,
            ) => (
              <Cell margin="0.9em 1.3em" key={key}>
                <ItemTable>
                  <tbody>
                    <tr>
                      <HeaderAnimal
                        role="button"
                        style={{ backgroundColor: 'rgb(242, 240, 235);' }}
                        onClick={() => onToggleExpandHide(key)}
                      >
                        Oppr.merke
                      </HeaderAnimal>
                      <HeaderAnimal onClick={() => onToggleExpandHide(key)}>
                        {animal_keeping_no}
                        {' '}
                        /
                        {' '}
                        {(`${individual_no}`).padStart(5, '0')}
                      </HeaderAnimal>
                      <HeaderAnimal onClick={() => onToggleExpandHide(key)}>
                        <UpDownArrow
                          style={{ top: '15px' }}
                          aria-hidden="true"
                          down={expandedIndexes[key]}
                        />
                      </HeaderAnimal>
                    </tr>
                  </tbody>
                </ItemTable>

                <motion.div
                  variants={expandCollapseVariants}
                  animate={expandedIndexes[key] ? 'visible' : 'collapsed'}
                >
                  {expandedIndexes[key] && (
                    <ItemTable style={{ borderTop: 'none' }}>
                      <tbody>
                        <tr>
                          <td>Vare</td>
                          <td>{textCase(cattle_type)}</td>
                        </tr>
                        <tr>
                          <td>Rase</td>
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                              }}
                            >
                              {breeds
                                && breeds.map(({ name, perc }, key) => (
                                  <span
                                    style={{
                                      flex: '0 1 max-content',
                                    }}
                                  >
                                    {name}
                                    {' '}
                                    {perc}
                                    %
                                    {key < breeds.length - 1 && <>,&nbsp;</>}
                                  </span>
                                ))}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Kalvetidspunkt</td>
                          <td>
                            {veal_date
                              && format(parseISO(veal_date), 'dd.MM.yyyy')}
                          </td>
                        </tr>
                        <tr>
                          <td>Født</td>
                          <td>
                            {birthdate
                              && format(parseISO(birthdate), 'dd.MM.yyyy')}
                          </td>
                        </tr>
                      </tbody>
                    </ItemTable>
                  )}
                </motion.div>
              </Cell>
            ),
          )}
        </>
      )}
      {error && !loading && (
        <Row
          justify="center"
          padding="1.3em 0"
          style={{ flexWrap: 'wrap', padding: '2em' }}
        >
          <Label>
            <Icon inline icon={faInfoCircle} marginRight="0.5em" />
            Det oppstod en feil ved innlasting av data, eller du har ikke
            registrerte storfe hos Mattilsynet. .&nbsp;
          </Label>
          <LinkLabel inline onClick={() => setRetry((retry) => retry + 1)}>
            Prøv på nytt
          </LinkLabel>
        </Row>
      )}
    </Page>
  );
};

export default MyCattle;
